import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The design's settings card: a titled group of rows, each a label and a short
 * description with its control on the right. Transcribed from the shared
 * settings screen (admin, customer and ops all use the one layout).
 */
export function SettingsGroup({
  title,
  sub,
  children,
}: {
  title: string;
  sub: string;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-[0_1px_3px_rgba(15,23,42,.05)]">
      <header className="border-b border-border/60 px-7 py-5.5">
        <h2 className="text-base font-bold">{title}</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">{sub}</p>
      </header>
      {children}
    </section>
  );
}

export function SettingsRow({
  label,
  desc,
  control,
  children,
  className,
}: {
  label: string;
  desc: ReactNode;
  control?: ReactNode;
  /** Anything that opens under the row - a setup step, say. */
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('border-b border-border/40 px-7 py-5 last:border-b-0', className)}>
      <div className="flex items-center gap-5">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{label}</div>
          <div className="mt-0.75 text-[12.5px] leading-normal text-muted-foreground">{desc}</div>
        </div>
        {control && <div className="flex flex-none items-center gap-2">{control}</div>}
      </div>
      {children}
    </div>
  );
}
